/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */
import { EventEmitter } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { Observable } from 'rxjs';
import { NvHigBottomSheetComponent } from '../nv-hig-bottom-sheet/nv-hig-bottom-sheet.component';
import { NvHigDialogComponent } from '../nv-hig-dialog/nv-hig-dialog.component';
import { NvHigThreeQuarterSheetComponent } from '../nv-hig-three-quarter-sheet/nv-hig-three-quarter-sheet.component';
import { NvHigDialogRef, NvHigBottomSheetRef, NvHigThreeQuarterSheetRef } from './nv-generic-dialog.types';

export class NvHigGenericDialogRef {
    constructor(private reference: MatDialogRef<any> | MatBottomSheetRef<any>) {}

    private get dialogRef(): NvHigDialogRef {
        return this.reference instanceof MatDialogRef &&
            this.reference.componentInstance instanceof NvHigDialogComponent
            ? this.reference
            : undefined;
    }

    private get bottomSheetRef(): NvHigBottomSheetRef {
        return this.reference instanceof MatBottomSheetRef &&
            this.reference.instance instanceof NvHigBottomSheetComponent
            ? this.reference
            : undefined;
    }

    private get threeQuarterSheetRef(): NvHigThreeQuarterSheetRef {
        return this.reference instanceof MatDialogRef &&
            this.reference.componentInstance instanceof NvHigThreeQuarterSheetComponent
            ? this.reference
            : undefined;
    }

    private get instance() {
        return (
            this.dialogRef?.componentInstance ??
            this.bottomSheetRef?.instance ??
            this.threeQuarterSheetRef?.componentInstance
        );
    }

    get primaryButtonClick(): EventEmitter<MouseEvent> {
        return this.instance?.primaryButtonClick;
    }

    get secondaryButtonClick(): EventEmitter<MouseEvent> {
        return this.instance?.secondaryButtonClick;
    }

    get linkClick(): EventEmitter<any> {
        return this.instance?.linkClick;
    }

    get dialogKeydownEvents(): Observable<KeyboardEvent> {
        return this.dialogRef?.keydownEvents();
    }

    /**
     * emitted string of button.id to tell which button of augmentedButtons is clicked.
     */
    get augmentedButtonClick(): EventEmitter<string> {
        return this.instance instanceof NvHigThreeQuarterSheetComponent
            ? this.instance.augmentedButtonClick
            : undefined;
    }

    get radioButtonClick(): EventEmitter<any> {
        return this.instance instanceof NvHigDialogComponent || this.instance instanceof NvHigThreeQuarterSheetComponent
            ? this.instance.radioButtonClick
            : undefined;
    }

    get checkboxState(): boolean {
        return this.instance?.data?.checkboxState;
    }

    get radioOptionSelected(): string {
        return this.instance?.data?.radioOptionSelected;
    }

    /**
     * This is used by BackToCloseModalDirective#close method which will be the result
     * to return to the dialog/bottom-sheet opener
     */
    set backToCloseResult(result: any) {
        this.instance.backToCloseResult = result;
    }

    close(result?: any) {
        this.dialogRef?.close(result);
        this.bottomSheetRef?.dismiss(result);
        this.threeQuarterSheetRef?.close(result);
    }

    afterClosed() {
        return (
            this.dialogRef?.afterClosed() ??
            this.bottomSheetRef?.afterDismissed() ??
            this.threeQuarterSheetRef?.afterClosed()
        );
    }

    afterOpened() {
        return (
            this.dialogRef?.afterOpened() ??
            this.bottomSheetRef?.afterOpened() ??
            this.threeQuarterSheetRef?.afterOpened()
        );
    }
}
